/*
 * Copyright (c) 2019 Frontier Developments PLC.
 */

import {LocaleAdapterInterface} from '@/services/language/types';
import {I18nAdapter, MomentAdapter, PersistenceAdapter, VeeValidateAdapter} from '@/services/language/adapter';

/**
 * Api service class used to leverage ajax library in order to communicate with api.
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
export class LanguageService {
    /**
     * Language map used to determine which browser languages map to which translation files.
     *
     * @type {Map}
     */
    private dictionary: Map<string, string> = new Map([
        ['cs', 'cs'],
        ['cz', 'cs'],
        ['da', 'da'],
        ['de', 'de'],
        ['de-de', 'de'],
        ['en', 'en'],
        ['en-us', 'en'],
        ['en-gb', 'en'],
        ['en-au', 'en'],
        ['en-ca', 'en'],
        ['es', 'es'],
        ['es-es', 'es'],
        ['es-mx', 'es'],
        ['fr', 'fr-fr'],
        ['fr-fr', 'fr-fr'],
        ['it', 'it'],
        ['ja', 'ja'],
        ['jp', 'ja'],
        ['ja-jp', 'ja'],
        ['ko', 'ko'],
        ['ko-ko', 'ko'],
        ['nl', 'nl'],
        ['no', 'no'],
        ['nb', 'no'],
        ['pl', 'pl'],
        ['pt', 'pt-br'],
        ['pt-br', 'pt-br'],
        ['pt-pt', 'pt-br'],
        ['ru', 'ru'],
        ['sv', 'sv'],
        ['zh', 'zh-cn'],
        ['zh-zh', 'zh-cn'],
        ['zh-cn', 'zh-cn'],
        ['zh-tw', 'zh-tw'],
        ['zh-hans', 'zh-cn'],
        ['zh-hant', 'zh-tw'],
    ]);

    /**
     * The set of supported languages.
     */
    private supported: string[] = [...new Set(Array.from(this.dictionary.values()))];

    /**
     * Selected locale.
     */
    private locale!: string;

    /**
     * Adapter set.
     */
    private adapters: LocaleAdapterInterface[] = [];

    /**
     * @param adapters
     */
    constructor(adapters: LocaleAdapterInterface[] = []) {
        this.addApaters(adapters);
    }

    /**
     * Add an adapter.
     *
     * @param adapter
     */
    public addAdapter(adapter: LocaleAdapterInterface): void {
        this.adapters.push(adapter);
    }

    /**
     * Add a set of adapters.
     *
     * @param adapters
     */
    public addApaters(adapters: LocaleAdapterInterface[]): void {
        for (const adapter of adapters) {
            this.addAdapter(adapter);
        }
    }

    /**
     * Getter for supported langauges.
     *
     * @returns {string[]}
     */
    public getSupported(): string[] {
        return this.supported;
    }

    /**
     * Getter for selected locale.
     *
     * @return {string}
     */
    public getSelectedLocale(): string {
        return this.locale.replace('_', '-');
    }

    /**
     * Find the language from a given locale.
     *
     * @param locale
     */
    public findLanguage(locale: string): string {
        return this.dictionary.get(locale) ||
            this.dictionary.get(locale.replace(/[_-].*/, '')) ||
            'en';
    }

    /**
     * Set the language.
     *
     * @param language
     */
    public setLanguage(language: string): void {
        // load saved language selection
        const locale: string = this.findLanguage(language.toLowerCase());

        // check for current language
        if (locale === this.locale) {
            return;
        }

        // update locale
        this.locale = locale;

        // set language using adapters
        for (const adapter of this.adapters) {
            adapter.setLocale(this.locale);
        }
    }
}

export default new LanguageService([
    new I18nAdapter(),
    new PersistenceAdapter(),
    new MomentAdapter(),
    new VeeValidateAdapter(),
]);
